import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import Layout from "./Layout";
import "./style/App.css";
import { AuthProvider } from "./contexts/AuthContext";
import { ReportProvider } from "./contexts/ReportContext";
import { ToolbarProvider } from "./contexts/ToolbarContext";
import { registerLicense } from "@syncfusion/ej2-base";
import { LicenseInfo } from '@mui/x-license';
import { ColorModeProvider } from "./contexts/ColorModeProvider";
import { ColorPaletteProvider } from "./contexts/ColorPaletteProvider";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";
import { DataEnvironmentProvider } from "./contexts/DataEnvironmentContext";
import { TreeProvider } from "./contexts/TreeContext";
import { MapProvider } from "./contexts/MapContext";
import { IconsProvider } from "./contexts/IconsContext";
import { SurfaceAccessProvider, useSurfaceAccess } from "./contexts/SurfaceAccessContext";
import type { SurfaceKey } from "./authz/surfaceAccess";
import { resolveWindowTitle } from "./utils/windowTitle";
import AccessUnavailable from "./components/AccessUnavailable";
import { apiService } from "./api/client";
import { LoadingState } from "./components/common/LoadingStates";

// Lazy load components
const Login = lazy(() => import("./components/Login"));
const SiteSessionBridge = lazy(() => import("./components/SiteSessionBridge"));
const Logout = lazy(() => import("./components/Logout"));
const EditProfile = lazy(() => import("./components/EditProfile"));
const CategoriesAdmin = lazy(() => import("./components/CategoriesAdmin"));
const ReportPage = lazy(() => import("./components/ReportPage"));
const DevicesReport = lazy(() => import("./components/DevicesReport"));
const ProductsReport = lazy(() => import("./components/ProductsReport"));
const CategoriesReport = lazy(() => import("./components/CategoriesReport"));
const ZonesReport = lazy(() => import("./components/ZonesReport"));
const EventsReport = lazy(() => import("./components/EventsReport"));
const VenuesReport = lazy(() => import("./components/VenuesReport"));
const ProductsAdmin = lazy(() => import("./components/ProductsAdmin"));
const DevicesAdmin = lazy(() => import("./components/DevicesAdmin"));
const Readings = lazy(() => import("./components/Readings"));
const AgentControl = lazy(() => import("./components/AgentControl"));
const AgentLog = lazy(() => import("./components/AgentLog"));
const Signup = lazy(() => import("./components/Signup"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ConfirmSignup = lazy(() => import("./components/ConfirmSignup"));
const PendingApproval = lazy(() => import("./components/PendingApproval"));
const PendingVerification = lazy(() => import("./components/PendingVerification"));
const AccountRejected = lazy(() => import("./components/AccountRejected"));
const NotFound = lazy(() => import("./components/NotFound"));
const DevicesPage = lazy(() => import("./pages/DevicesPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const VenuesPage = lazy(() => import("./pages/VenuesPage"));
const ThemeInfo = lazy(() => import("./components/ThemeInfo"));
const Home = lazy(() => import("./components/Home"));
const GetStarted = lazy(() => import("./components/GetStarted"));
const People = lazy(() => import("./components/People"));
const Groups = lazy(() => import("./components/Groups"));
const MessageCenter = lazy(() => import("./components/MessageCenter"));
const MessageCenterFix = lazy(() => import("./components/MessageCenterFix"));
const EventMap = lazy(() => import("./components/EventMap"));
const VenueMap = lazy(() => import("./components/VenueMap"));
const ZoneMap = lazy(() => import("./components/ZoneMap"));
const NewVenue = lazy(() => import("./components/NewVenue"));
const NewEvent = lazy(() => import("./components/NewEvent"));

registerLicense(
  "MzkzNjAwMkAzMzMwMmUzMDJlMzAzYjMzMzAzYlRRYVJaa0d0MGV1VlV1NlI0TlNUV2pQV2lLSSs2VkxFRWRPalRzRVJ5SkE9;MzkzNjAwM0AzMzMwMmUzMDJlMzAzYjMzMzAzYkxQaUQ2T2MzMWpPb000WTI5SjhXcGVsRWxRb2NRanp6eExBdHMrSUJSWUU9;Mgo+DSMBPh8sVXN0S0d+X1ZPd11dXmJWd1p/THNYflR1fV9DaUwxOX1dQl9mSXlTckVmWndaeHFSRGBXUkQ=;ORg4AjUWIQA/Gnt3VVhhQlJDfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5UdkFjXHxWdHxTRmNeWkd2;NRAiBiAaIQQuGjN/VkZ+XU9HcVRDX3xKf0x/TGpQb19xflBPallYVBYiSV9jS3tTcUVnWXpdeHBdQGNYVE91Xw==;MzkzNjAwN0AzMzMwMmUzMDJlMzAzYjMzMzAzYlZHMUk5VEp4UkJOMEZIQlNxM3pFUU9SanVVeDdZajVBTkJ6ZENUcDg1OFk9;MzkzNjAwOEAzMzMwMmUzMDJlMzAzYjMzMzAzYmJNWVhOa3Z2SXRyTmNBYXA4dmRNUlM1QUxsZEFYU1hWWmc2VWxpaHZ3bnc9;Mgo+DSMBMAY9C3t3VVhhQlJDfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5UdkFjXHxWdHxSR2NaWkd2;MzkzNjAxMEAzMzMwMmUzMDJlMzAzYjMzMzAzYlVSUXRIeTROQXZhbnJweE1TMmI5ZkhCOCtFdG1NQzdUbE51NTBSNXA2Mjg9;MzkzNjAxMUAzMzMwMmUzMDJlMzAzYjMzMzAzYlNqOVU3L2VqMkVrZ1NlMG9UakI1OUxYSmphVGRlZmpGeWtxZVdZcW1TVzQ9;MzkzNjAxMkAzMzMwMmUzMDJlMzAzYjMzMzAzYlZHMUk5VEp4UkJOMEZIQlNxM3pFUU9SanVVeDdZajVBTkJ6ZENUcDg1OFk9"
);

LicenseInfo.setLicenseKey(
  "52c7226c6fd41e419d43d1bc575d4131Tz0xMTE0OTAsRT0xNzc2MzgzOTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

const LoadingSpinner = ({ label = "Loading App" }: { label?: string }) => (
  <LoadingState label={label} minHeight="100%" size="standard" />
);

const SurfaceRoute = ({
  surface,
  children,
}: {
  surface: SurfaceKey;
  children: React.ReactElement;
}) => {
  const { isLoading, getSurfaceAccess } = useSurfaceAccess();

  if (isLoading) {
    return <LoadingSpinner label={`Loading ${surface}`} />;
  }

  const access = getSurfaceAccess(surface);
  if (!access.allowed) {
    return <AccessUnavailable message={access.message} />;
  }

  return children;
};

const DeviceEngineeringRoute = () => {
  const { deviceId } = useParams();
  const { isLoading, getSurfaceAccess, canEngineerOwnerGroup } = useSurfaceAccess();
  const [ownerGroupId, setOwnerGroupId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    let ignore = false;

    const fetchDeviceOwner = async () => {
      if (!deviceId) {
        setOwnerGroupId(null);
        return;
      }

      try {
        const device = await apiService.get("instances", Number(deviceId));
        if (!ignore) {
          setOwnerGroupId(device.owner_group_id ?? null);
        }
      } catch (err) {
        console.error("Failed to load device owner group:", err);
        if (!ignore) {
          setOwnerGroupId(null);
        }
      }
    };

    void fetchDeviceOwner();

    return () => {
      ignore = true;
    };
  }, [deviceId]);

  if (isLoading || ownerGroupId === undefined) {
    return <LoadingSpinner label="Loading Device Engineering" />;
  }

  const surfaceAccess = getSurfaceAccess("engineering");
  if (!surfaceAccess.allowed) {
    return <AccessUnavailable message={surfaceAccess.message} />;
  }

  if (!canEngineerOwnerGroup(ownerGroupId)) {
    return <AccessUnavailable message="Device Engineering is available only for Devices owned by Groups where you are an Engineer." />;
  }

  return <DevicesAdmin />;
};

const App = () => {
  useEffect(() => {
    document.title = resolveWindowTitle(process.env.REACT_APP_WINDOW_TITLE);
  }, []);

  return (
    <AuthProvider>
      <UserPreferencesProvider>
        <DataEnvironmentProvider>
          <ColorModeProvider>
            <SurfaceAccessProvider>
            <ColorPaletteProvider>
              <ReportProvider>
                <ToolbarProvider>
                  <TreeProvider>
                    <MapProvider>
                      <IconsProvider>
                        <Router>
                          <Routes>
                          {/* Public Routes - No Authentication Required */}
                          <Route path="login" element={<Login />} />
                          <Route path="auth/site-session" element={<SiteSessionBridge />} />
                          <Route path="logout" element={<Logout />} />
                          <Route path="signup" element={<Signup />} />
                          <Route path="forgot-password" element={<ForgotPassword />} />
                          <Route path="confirm-signup" element={<ConfirmSignup />} />
                          <Route path="pending-approval" element={<PendingApproval />} />
                          <Route path="pending-verification" element={<PendingVerification />} />
                          <Route path="account-rejected" element={<AccountRejected />} />

                          {/* Authenticated Routes - Requires AuthProvider */}
                          <Route
                            element={
                              <Suspense fallback={<LoadingSpinner />}>
                                <Layout />
                              </Suspense>
                            }
                          >
                            {/* Banner-linked routes */}
                            <Route path="edit-profile" element={<EditProfile />} />

                            <Route path="readings" element={<Readings />} />
                            <Route path="agent" element={<Navigate to="/agents" replace />} />
                            <Route path="agents" element={<SurfaceRoute surface="agents"><AgentControl /></SurfaceRoute>} />
                            <Route path="agent-log" element={<SurfaceRoute surface="agentLog"><AgentLog /></SurfaceRoute>} />

                            <Route path="report" element={<ReportPage />} />

                            {/* Routes for the Reports Overview tree diagram */}
                            <Route path="device-report/:deviceId" element={<DevicesReport />} />
                            <Route path="category-report/:categoryId" element={<CategoriesReport />} />
                            <Route path="product-report/:productId" element={<ProductsReport />} />
                            <Route path="zone-report/:zoneId" element={<ZonesReport />} />
                            <Route path="event-report/:eventId" element={<EventsReport />} />
                            <Route path="venue-report/:venueId" element={<VenuesReport />} />


                            {/* Routes for Maps of Events, Venues and Zones */}
                            <Route path="event-map/:eventId" element={<EventMap />} />
                            <Route path="venue-map/:venueId" element={<VenueMap />} />
                            <Route path="zone-map/:zoneId" element={<ZoneMap />} />

                            {/* Routes for the Administration Overview tree diagram */}
                            <Route path="device-engineering/:deviceId" element={<DeviceEngineeringRoute />} />
                            <Route path="category-engineering/:categoryId" element={<SurfaceRoute surface="engineering"><CategoriesAdmin /></SurfaceRoute>} />
                            <Route path="product-engineering/:productId" element={<SurfaceRoute surface="engineering"><ProductsAdmin /></SurfaceRoute>} />

                            {/* Default Redirect */}
                            {/* <Route path="/" element={<Home />} /> */}
                            <Route path="/" element={<Home />} />
                            <Route path="get-started" element={<GetStarted />} />

                            {/* Routes for the Tree Overview */}
                            <Route path="devices" element={<DevicesPage />} />
                            <Route path="events" element={<EventsPage />} />
                            <Route path="venues" element={<VenuesPage />} />
                            <Route path="events/new" element={<NewEvent />} />
                            <Route path="venues/new" element={<NewVenue />} />
                            <Route path="people" element={<SurfaceRoute surface="people"><People /></SurfaceRoute>} />
                            <Route path="groups" element={<SurfaceRoute surface="groups"><Groups /></SurfaceRoute>} />
                            <Route path="messages" element={<MessageCenter />} />
                            <Route path="messages/:messageId/fix" element={<MessageCenterFix />} />

                            <Route path="theme-info" element={<SurfaceRoute surface="diagnostics"><ThemeInfo /></SurfaceRoute>} />

                            {/* Catch-All for Undefined Routes */}
                            <Route path="*" element={<NotFound />} />
                          </Route>
                          </Routes>
                        </Router>
                      </IconsProvider>
                    </MapProvider>
                  </TreeProvider>
                </ToolbarProvider>
              </ReportProvider>
            </ColorPaletteProvider>
            </SurfaceAccessProvider>
          </ColorModeProvider>
        </DataEnvironmentProvider>
      </UserPreferencesProvider>
    </AuthProvider>
  );
};

export default App;
